import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, LayoutGrid, Gift, Sparkles, Users, HelpCircle, Play, BarChart3, Gauge, Trophy, MapPin, Mail, CheckCircle2 } from 'lucide-react';
interface VisualStoryboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}
interface Panel {
  step: string;
  title: string;
  caption: string;
  visual: React.ReactNode;
  tag: string;
  tagColor: string;
}
// Mini phone frame wrapper
function Phone({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="relative w-full aspect-[9/16] rounded-[18px] bg-gradient-to-br from-[#EAF2FA] via-[#F4F8FC] to-[#DCE7F4] border border-slate-200 shadow-inner overflow-hidden" data-id="element-287">
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-12 h-1 rounded-full bg-slate-300/60" data-id="element-288" />
      <div className="absolute inset-0 p-2.5 pt-4 flex flex-col" data-id="element-289">
        {children}
      </div>
    </div>;
}
function MiniLogo() {
  return <div className="text-[7px] font-display font-bold tracking-[0.2em] text-minerva-blue uppercase" data-id="element-290">
      Minerva
    </div>;
}
const PANELS: Panel[] = [{
  step: '1',
  title: 'Screensaver',
  caption: 'Floating prize images draw delegates in. Pure attraction.',
  tag: 'Aisle Breaker',
  tagColor: 'bg-amber-100 text-amber-700',
  visual: <Phone data-id="element-291">
        <div className="flex-1 relative" data-id="element-292">
          {/* floating prize dots */}
          <div className="absolute top-2 left-2 w-7 h-7 rounded-lg bg-white shadow-sm border border-slate-100 flex items-center justify-center" data-id="element-293">
            <Gift size={10} className="text-minerva-blue" data-id="element-294" />
          </div>
          <div className="absolute top-3 right-2 w-7 h-7 rounded-lg bg-white shadow-sm border border-slate-100 flex items-center justify-center" data-id="element-295">
            <Trophy size={10} className="text-amber-500" data-id="element-296" />
          </div>
          <div className="absolute bottom-10 left-3 w-6 h-6 rounded-lg bg-white shadow-sm border border-slate-100" data-id="element-297" />
          <div className="absolute bottom-12 right-3 w-6 h-6 rounded-lg bg-white shadow-sm border border-slate-100" data-id="element-298" />
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-299">
            <div className="w-7 h-7 rounded-full bg-minerva-blue flex items-center justify-center mb-1.5" data-id="element-300">
              <Gift size={12} className="text-white" data-id="element-301" />
            </div>
            <div className="text-[8px] font-bold text-slate-900 text-center leading-tight" data-id="element-302">
              Win Premium
              <br data-id="element-303" />
              Prizes
            </div>
            <div className="mt-1.5 text-[6px] font-bold tracking-widest uppercase bg-slate-900 text-white px-2 py-0.5 rounded-full" data-id="element-304">
              Tap to begin
            </div>
          </div>
        </div>
      </Phone>
}, {
  step: '2',
  title: 'Intro / Hook',
  caption: 'Minerva + SNMMI logos, "See what your peers think" promise, prize list + booth double odds.',
  tag: 'Hook + Pathos',
  tagColor: 'bg-minerva-blue/10 text-minerva-blue',
  visual: <Phone data-id="element-305">
        <MiniLogo data-id="element-306" />
        <div className="flex-1 mt-1.5 flex flex-col" data-id="element-307">
          <div className="text-[6px] font-bold tracking-widest uppercase text-minerva-blue mb-1" data-id="element-308">
            SNMMI 2026
          </div>
          <div className="text-[9px] font-bold text-slate-900 leading-tight mb-1.5" data-id="element-309">
            The Industry
            <br data-id="element-310" />
            Pulse
          </div>
          <div className="text-[6px] text-slate-500 leading-tight mb-2" data-id="element-311">
            Compare your radiopharma perspective with your peers.
          </div>
          <div className="bg-white/80 rounded-md p-1 border border-slate-200 mb-1.5" data-id="element-312">
            <div className="text-[5px] font-bold tracking-widest uppercase text-amber-700 mb-0.5" data-id="element-313">
              Daily Draw
            </div>
            <div className="text-[6px] text-slate-700 leading-tight" data-id="element-314">
              AirPods · Watch · Bose · Sonos · Kindle
            </div>
          </div>
          <div className="mt-auto bg-slate-900 text-white text-[6px] font-bold text-center py-1 rounded-full" data-id="element-315">
            Start the challenge
          </div>
        </div>
      </Phone>
}, {
  step: '3',
  title: 'About You',
  caption: 'Segmentation: scientist / program lead / CMC / BD / exec.',
  tag: 'Segmentation',
  tagColor: 'bg-slate-100 text-slate-700',
  visual: <Phone data-id="element-316">
        <MiniLogo data-id="element-317" />
        <div className="mt-1.5 text-[8px] font-bold text-slate-900 leading-tight mb-2" data-id="element-318">
          Which best describes you?
        </div>
        <div className="space-y-1" data-id="element-319">
          {['Translational scientist', 'Program leader', 'CMC / Manufacturing', 'Business Development'].map(t => <div key={t} className="bg-white border border-slate-200 rounded-md py-1 px-1.5 text-[6px] text-slate-700" data-id="element-320">
              {t}
            </div>)}
        </div>
      </Phone>
}, {
  step: '4',
  title: 'Question 1 — Bottleneck',
  caption: 'What is the biggest bottleneck in radiopharma development today?',
  tag: 'Industry Q',
  tagColor: 'bg-minerva-blue/10 text-minerva-blue',
  visual: <Phone data-id="element-321">
        <MiniLogo data-id="element-322" />
        <div className="mt-1.5 text-[8px] font-bold text-slate-900 leading-tight mb-1.5" data-id="element-323">
          Biggest bottleneck?
        </div>
        <div className="space-y-1" data-id="element-324">
          {['Scientific translation', 'Manufacturing readiness', 'Workflow coordination', 'Clinical scalability'].map((t, i) => <div key={t} className={`rounded-md py-1 px-1.5 text-[6px] border ${i === 1 ? 'bg-minerva-blue text-white border-minerva-blue' : 'bg-white border-slate-200 text-slate-700'}`} data-id="element-325">
              {t}
            </div>)}
        </div>
      </Phone>
}, {
  step: '5',
  title: 'Reward 1 — Peer Insight',
  caption: 'Cinematic video, "X% answered like you", authoritative industry quote.',
  tag: 'Reward',
  tagColor: 'bg-emerald-100 text-emerald-700',
  visual: <Phone data-id="element-326">
        <div className="flex-1 -mx-2.5 -mt-4 relative bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden" data-id="element-327">
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-328">
            <Play size={18} className="text-white/80" data-id="element-329" />
          </div>
          <div className="absolute top-1 right-1 text-[5px] font-bold text-white/80 bg-white/10 px-1 py-0.5 rounded-full backdrop-blur" data-id="element-330">
            LIVE
          </div>
        </div>
        <div className="-mx-2.5 -mb-2.5 px-2 pt-2 pb-2 bg-white" data-id="element-331">
          <div className="text-[6px] font-bold text-minerva-blue mb-0.5" data-id="element-332">
            34% answered like you
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-1" data-id="element-333">
            <div className="h-full w-1/3 bg-minerva-blue rounded-full" data-id="element-334" />
          </div>
          <div className="text-[6px] italic text-slate-700 leading-tight" data-id="element-335">
            "The science may be elegant. The workflow rarely is."
          </div>
        </div>
      </Phone>
}, {
  step: '6',
  title: 'Brand Perception 1',
  caption: 'Bayesian scale: how strongly do peers associate Minerva with X?',
  tag: 'Insight Data',
  tagColor: 'bg-purple-100 text-purple-700',
  visual: <Phone data-id="element-336">
        <MiniLogo data-id="element-337" />
        <div className="mt-1.5 text-[7px] text-slate-600 leading-tight mb-0.5" data-id="element-338">
          How strongly do peers associate Minerva with:
        </div>
        <div className="inline-flex text-[6px] font-bold bg-minerva-blue/10 text-minerva-blue px-1.5 py-0.5 rounded-full mb-2 self-start" data-id="element-339">
          Manufacturing readiness
        </div>
        <div className="flex gap-1 justify-between" data-id="element-340">
          {[1, 2, 3, 4, 5].map(n => <div key={n} className={`flex-1 aspect-square rounded-md border flex items-center justify-center text-[7px] font-bold ${n === 4 ? 'bg-minerva-blue text-white border-minerva-blue' : 'bg-white border-slate-200 text-slate-500'}`} data-id="element-341">
              {n}
            </div>)}
        </div>
        <div className="flex justify-between mt-1 text-[5px] text-slate-400 uppercase tracking-wider" data-id="element-342">
          <span data-id="element-343">Not at all</span>
          <span data-id="element-344">Strongly</span>
        </div>
      </Phone>
}, {
  step: '7',
  title: 'Question 2 — Decision Factor',
  caption: 'What matters most when choosing a development partner?',
  tag: 'Industry Q',
  tagColor: 'bg-minerva-blue/10 text-minerva-blue',
  visual: <Phone data-id="element-345">
        <MiniLogo data-id="element-346" />
        <div className="mt-1.5 text-[8px] font-bold text-slate-900 leading-tight mb-1.5" data-id="element-347">
          What matters most in partner selection?
        </div>
        <div className="space-y-1" data-id="element-348">
          {['Scientific expertise', 'Speed & responsiveness', 'Integrated capabilities', 'GMP / regulatory'].map((t, i) => <div key={t} className={`rounded-md py-1 px-1.5 text-[6px] border ${i === 2 ? 'bg-minerva-blue text-white border-minerva-blue' : 'bg-white border-slate-200 text-slate-700'}`} data-id="element-349">
              {t}
            </div>)}
        </div>
      </Phone>
}, {
  step: '8',
  title: 'Reward 2 — Peer Insight',
  caption: 'Tumor imaging video, peer comparison, leadership quote.',
  tag: 'Reward',
  tagColor: 'bg-emerald-100 text-emerald-700',
  visual: <Phone data-id="element-350">
        <div className="flex-1 -mx-2.5 -mt-4 relative bg-gradient-to-br from-cyan-700 to-minerva-blue overflow-hidden" data-id="element-351">
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-352">
            <Play size={18} className="text-white/80" data-id="element-353" />
          </div>
        </div>
        <div className="-mx-2.5 -mb-2.5 px-2 pt-2 pb-2 bg-white" data-id="element-354">
          <div className="text-[6px] font-bold text-minerva-blue mb-0.5" data-id="element-355">
            28% answered like you
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-1" data-id="element-356">
            <div className="h-full w-[28%] bg-minerva-blue rounded-full" data-id="element-357" />
          </div>
          <div className="text-[6px] italic text-slate-700 leading-tight" data-id="element-358">
            "Strong science gets attention. Reliable execution wins programs."
          </div>
        </div>
      </Phone>
}, {
  step: '9',
  title: 'Brand Perception 2',
  caption: 'How peers associate Minerva with the decision driver.',
  tag: 'Insight Data',
  tagColor: 'bg-purple-100 text-purple-700',
  visual: <Phone data-id="element-359">
        <MiniLogo data-id="element-360" />
        <div className="mt-1.5 text-[7px] text-slate-600 leading-tight mb-0.5" data-id="element-361">
          How strongly associated with:
        </div>
        <div className="inline-flex text-[6px] font-bold bg-minerva-blue/10 text-minerva-blue px-1.5 py-0.5 rounded-full mb-2 self-start" data-id="element-362">
          Integrated capabilities
        </div>
        <div className="flex gap-1 justify-between" data-id="element-363">
          {[1, 2, 3, 4, 5].map(n => <div key={n} className={`flex-1 aspect-square rounded-md border flex items-center justify-center text-[7px] font-bold ${n === 5 ? 'bg-minerva-blue text-white border-minerva-blue' : 'bg-white border-slate-200 text-slate-500'}`} data-id="element-364">
              {n}
            </div>)}
        </div>
      </Phone>
}, {
  step: '10',
  title: 'Question 3 — Future Need',
  caption: 'What capability will matter most in 3–5 years?',
  tag: 'Industry Q',
  tagColor: 'bg-minerva-blue/10 text-minerva-blue',
  visual: <Phone data-id="element-365">
        <MiniLogo data-id="element-366" />
        <div className="mt-1.5 text-[8px] font-bold text-slate-900 leading-tight mb-1.5" data-id="element-367">
          Most critical capability in 3-5 years?
        </div>
        <div className="space-y-1" data-id="element-368">
          {['Translational expertise', 'Manufacturing scalability', 'Supply chain reliability', 'Integrated workflows'].map((t, i) => <div key={t} className={`rounded-md py-1 px-1.5 text-[6px] border ${i === 3 ? 'bg-minerva-blue text-white border-minerva-blue' : 'bg-white border-slate-200 text-slate-700'}`} data-id="element-369">
              {t}
            </div>)}
        </div>
      </Phone>
}, {
  step: '11',
  title: 'Reward 3 — Strategic Insight',
  caption: 'Premium image, peer comparison, strategic development perspective.',
  tag: 'Reward',
  tagColor: 'bg-emerald-100 text-emerald-700',
  visual: <Phone data-id="element-370">
        <div className="flex-1 -mx-2.5 -mt-4 relative bg-gradient-to-br from-indigo-700 to-slate-900 overflow-hidden" data-id="element-371">
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-372">
            <Sparkles size={16} className="text-white/80" data-id="element-373" />
          </div>
        </div>
        <div className="-mx-2.5 -mb-2.5 px-2 pt-2 pb-2 bg-white" data-id="element-374">
          <div className="text-[6px] font-bold text-minerva-blue mb-0.5" data-id="element-375">
            35% answered like you
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-1" data-id="element-376">
            <div className="h-full w-[35%] bg-minerva-blue rounded-full" data-id="element-377" />
          </div>
          <div className="text-[6px] italic text-slate-700 leading-tight" data-id="element-378">
            "The future challenge may not be better molecules—but less
            friction."
          </div>
        </div>
      </Phone>
}, {
  step: '12',
  title: 'Brand Perception 3',
  caption: 'Final Bayesian association on future capabilities.',
  tag: 'Insight Data',
  tagColor: 'bg-purple-100 text-purple-700',
  visual: <Phone data-id="element-379">
        <MiniLogo data-id="element-380" />
        <div className="mt-1.5 text-[7px] text-slate-600 leading-tight mb-0.5" data-id="element-381">
          Associated with future capability:
        </div>
        <div className="inline-flex text-[6px] font-bold bg-minerva-blue/10 text-minerva-blue px-1.5 py-0.5 rounded-full mb-2 self-start" data-id="element-382">
          Integrated workflows
        </div>
        <div className="flex gap-1 justify-between" data-id="element-383">
          {[1, 2, 3, 4, 5].map(n => <div key={n} className={`flex-1 aspect-square rounded-md border flex items-center justify-center text-[7px] font-bold ${n === 3 ? 'bg-minerva-blue text-white border-minerva-blue' : 'bg-white border-slate-200 text-slate-500'}`} data-id="element-384">
              {n}
            </div>)}
        </div>
      </Phone>
}, {
  step: '13',
  title: "You're in the Draw",
  caption: 'Lead capture: name, email, company, role → raffle entry.',
  tag: 'Conversion',
  tagColor: 'bg-amber-100 text-amber-700',
  visual: <Phone data-id="element-385">
        <MiniLogo data-id="element-386" />
        <div className="mt-1.5 text-[8px] font-bold text-slate-900 leading-tight mb-0.5" data-id="element-387">
          You're in the draw
        </div>
        <div className="text-[6px] text-slate-500 mb-1.5" data-id="element-388">
          Enter today's prize raffle
        </div>
        <div className="space-y-1" data-id="element-389">
          {['Name', 'Email', 'Company', 'Role'].map(t => <div key={t} className="bg-white border border-slate-200 rounded-md h-3 px-1.5 text-[5px] text-slate-400 flex items-center" data-id="element-390">
              {t}
            </div>)}
        </div>
        <div className="mt-1.5 bg-minerva-blue text-white text-[6px] font-bold text-center py-1 rounded-full" data-id="element-391">
          Enter today's draw
        </div>
        <div className="mt-1 text-[5px] text-amber-700 text-center font-semibold" data-id="element-392">
          2× chances at Booth #1762
        </div>
      </Phone>
}, {
  step: '14',
  title: 'Thank You',
  caption: 'Memory anchor: booth invite, follow links, restart.',
  tag: 'Memory Anchor',
  tagColor: 'bg-slate-100 text-slate-700',
  visual: <Phone data-id="element-393">
        <div className="flex-1 flex flex-col items-center justify-center" data-id="element-394">
          <div className="w-8 h-8 rounded-full bg-minerva-blue flex items-center justify-center mb-1.5" data-id="element-395">
            <CheckCircle2 size={14} className="text-white" data-id="element-396" />
          </div>
          <div className="text-[9px] font-bold text-slate-900 mb-0.5" data-id="element-397">
            You're in.
          </div>
          <div className="text-[6px] text-slate-500 text-center leading-tight px-2 mb-2" data-id="element-398">
            See you at Booth #1762 for double chances.
          </div>
          <div className="flex gap-1" data-id="element-399">
            <div className="w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center" data-id="element-400">
              <MapPin size={7} className="text-minerva-blue" data-id="element-401" />
            </div>
            <div className="w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center" data-id="element-402">
              <Mail size={7} className="text-minerva-blue" data-id="element-403" />
            </div>
          </div>
        </div>
      </Phone>
}];
export function VisualStoryboardModal({
  isOpen,
  onClose
}: VisualStoryboardModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const handleDownload = () => {
    window.print();
  };
  return <AnimatePresence data-id="element-404">
      {isOpen && <>
          {/* Print styles — hides everything except the storyboard sheet */}
          <style data-id="element-405">{`
            @media print {
              @page { size: A4 landscape; margin: 12mm; }
              body * { visibility: hidden !important; }
              #visual-storyboard-print, #visual-storyboard-print * {
                visibility: visible !important;
              }
              #visual-storyboard-print {
                position: absolute !important;
                inset: 0 !important;
                width: 100% !important;
                background: white !important;
                padding: 0 !important;
                overflow: visible !important;
              }
              .no-print { display: none !important; }
              .print-grid {
                display: grid !important;
                grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
                gap: 8mm !important;
              }
              .print-panel {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                box-shadow: none !important;
                border: 1px solid #e2e8f0 !important;
              }
            }
          `}</style>

          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[60] flex flex-col bg-slate-900/60 backdrop-blur-md no-print" data-id="element-406">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="mt-auto h-[94%] bg-white rounded-t-3xl shadow-2xl flex flex-col overflow-hidden" data-id="element-407">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white z-10 shrink-0 no-print" data-id="element-408">
                <div className="flex items-center gap-2" data-id="element-409">
                  <div className="w-8 h-8 rounded-full bg-minerva-blue/10 flex items-center justify-center" data-id="element-410">
                    <LayoutGrid size={16} className="text-minerva-blue" data-id="element-411" />
                  </div>
                  <div data-id="element-412">
                    <h3 className="text-sm font-bold text-slate-900" data-id="element-413">
                      Visual Storyboard
                    </h3>
                    <p className="text-[11px] font-medium text-slate-500" data-id="element-414">
                      Print-ready experience walkthrough
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-415">
                  <button onClick={handleDownload} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-minerva-blue text-white text-[11px] font-bold shadow-sm hover:bg-minerva-blue/90 transition-colors" data-id="element-416">
                    <Download size={13} data-id="element-417" />
                    Print / Save PDF
                  </button>
                  <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors" aria-label="Close" data-id="element-418">
                    <X size={18} data-id="element-419" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto bg-slate-50" data-id="element-420">
                <div id="visual-storyboard-print" ref={printRef} className="p-6 pb-12" data-id="element-421">
                  {/* Print-only title block */}
                  <div className="hidden print:block mb-6" data-id="element-422">
                    <div className="flex items-center justify-between border-b border-slate-300 pb-3 mb-3" data-id="element-423">
                      <div data-id="element-424">
                        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-minerva-blue" data-id="element-425">
                          Minerva Imaging · SNMMI 2026
                        </div>
                        <h1 className="text-xl font-display font-bold text-slate-900" data-id="element-426">
                          The Industry Pulse — Visual Storyboard
                        </h1>
                      </div>
                      <div className="text-right text-[10px] text-slate-500" data-id="element-427">
                        Aisle-breaker Snak™
                        <br data-id="element-428" />
                        Booth #1762
                      </div>
                    </div>
                  </div>

                  {/* On-screen title block */}
                  <div className="max-w-5xl mx-auto mb-6 print:hidden" data-id="element-429">
                    <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-minerva-blue mb-1" data-id="element-430">
                      Minerva Imaging · SNMMI 2026
                    </div>
                    <h1 className="text-2xl font-display font-bold text-slate-900 mb-1" data-id="element-431">
                      The Industry Pulse — Visual Storyboard
                    </h1>
                    <p className="text-sm text-slate-600" data-id="element-432">
                      A panel-by-panel walkthrough of the attendee experience.
                      Use the Print / Save PDF button above to export as a
                      shareable handout.
                    </p>
                  </div>

                  {/* Panels grid */}
                  <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print-grid" data-id="element-433">
                    {PANELS.map(panel => <div key={panel.step} className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm flex flex-col print-panel" data-id="element-434">
                        {/* Header row */}
                        <div className="flex items-center justify-between mb-2" data-id="element-435">
                          <div className="flex items-center gap-1.5" data-id="element-436">
                            <div className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center" data-id="element-437">
                              {panel.step}
                            </div>
                            <div className={`text-[8px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full ${panel.tagColor}`} data-id="element-438">
                              {panel.tag}
                            </div>
                          </div>
                        </div>

                        {/* Phone visual */}
                        <div className="mb-2" data-id="element-439">{panel.visual}</div>

                        {/* Title + caption */}
                        <div className="mt-auto" data-id="element-440">
                          <h4 className="text-[12px] font-bold text-slate-900 leading-tight mb-1" data-id="element-441">
                            {panel.title}
                          </h4>
                          <p className="text-[10px] text-slate-600 leading-snug" data-id="element-442">
                            {panel.caption}
                          </p>
                        </div>
                      </div>)}
                  </div>

                  {/* Footer / Legend */}
                  <div className="max-w-5xl mx-auto mt-8 pt-6 border-t border-slate-200" data-id="element-443">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]" data-id="element-444">
                      <div data-id="element-445">
                        <div className="font-bold text-slate-900 mb-1" data-id="element-446">
                          Aisle Breaker
                        </div>
                        <p className="text-slate-600 leading-snug" data-id="element-447">
                          An excuse to engage delegates passing by — visual
                          curiosity at the entry.
                        </p>
                      </div>
                      <div data-id="element-448">
                        <div className="font-bold text-slate-900 mb-1" data-id="element-449">
                          Strategic Conversation
                        </div>
                        <p className="text-slate-600 leading-snug" data-id="element-450">
                          Lifts Minerva above "bolts and nuts" into business
                          strategy territory.
                        </p>
                      </div>
                      <div data-id="element-451">
                        <div className="font-bold text-slate-900 mb-1" data-id="element-452">
                          Forefront Signal
                        </div>
                        <p className="text-slate-600 leading-snug" data-id="element-453">
                          Premium interactive experience signals technology
                          leadership.
                        </p>
                      </div>
                      <div data-id="element-454">
                        <div className="font-bold text-slate-900 mb-1" data-id="element-455">
                          Outcomes
                        </div>
                        <p className="text-slate-600 leading-snug" data-id="element-456">
                          Qualified leads · booth traffic · segmentation
                          intelligence · brand perception data.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 text-center text-[9px] text-slate-400 tracking-widest uppercase" data-id="element-457">
                      Minerva Imaging — Confidential Strategic Concept · SNMMI
                      2026
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}